import { useState } from 'react';
import { X, Plus, Trash2, TrendingUp } from 'lucide-react';
import { useEscalationPolicy, type EscalationPolicy, type EscalationTarget } from '@/lib/escalation';
import { cn } from '@/lib/utils';

export function EscalationEditor({
  open,
  teamId,
  teamName,
  onClose,
}: {
  open: boolean;
  teamId: string;
  teamName: string;
  onClose: () => void;
}) {
  const { policy, save } = useEscalationPolicy(teamId);
  const [draft, setDraft] = useState<EscalationPolicy>(policy);

  if (!open) return null;

  const setStep = (i: number, patch: Partial<{ afterMin: number; assignTo: EscalationTarget }>) =>
    setDraft((d) => ({ ...d, steps: d.steps.map((s, j) => (j === i ? { ...s, ...patch } : s)) }));

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-6 pt-[10vh]">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg overflow-hidden rounded-2xl border border-border bg-card shadow-2xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div>
            <div className="flex items-center gap-2 text-base font-semibold"><TrendingUp className="h-4 w-4 text-primary" /> Escalation chain</div>
            <div className="text-xs text-muted-foreground">Reassign unacknowledged {teamName} tickets on a timer</div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </div>

        <div className="space-y-4 p-5">
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input type="checkbox" checked={draft.enabled} onChange={(e) => setDraft((d) => ({ ...d, enabled: e.target.checked }))} className="accent-primary" />
            <span className="font-medium">Enable escalation for this team</span>
          </label>

          <div className="space-y-2">
            {draft.steps.map((s, i) => (
              <div key={i} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2">
                <span className="font-mono text-xs text-muted-foreground">L{i + 1}</span>
                <span className="text-sm text-muted-foreground">after</span>
                <input
                  type="number"
                  min="1"
                  value={s.afterMin}
                  onChange={(e) => setStep(i, { afterMin: Number(e.target.value) })}
                  className="h-8 w-20 rounded-md border border-input bg-background px-2 text-sm outline-none focus:border-primary"
                />
                <span className="text-sm text-muted-foreground">min → reassign to</span>
                <div className="flex rounded-md border border-border p-0.5 text-xs">
                  {(['oncall', 'lead'] as const).map((tgt) => (
                    <button
                      key={tgt}
                      onClick={() => setStep(i, { assignTo: tgt })}
                      className={cn('rounded px-2 py-0.5 font-medium transition', s.assignTo === tgt ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground')}
                    >
                      {tgt === 'oncall' ? 'On-call' : 'Team lead'}
                    </button>
                  ))}
                </div>
                <button onClick={() => setDraft((d) => ({ ...d, steps: d.steps.filter((_, j) => j !== i) }))} className="ml-auto text-muted-foreground hover:text-temp-ember">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
            <button
              onClick={() => setDraft((d) => ({ ...d, steps: [...d.steps, { afterMin: 60, assignTo: 'lead' }] }))}
              className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-border px-3 py-1.5 text-sm font-medium text-muted-foreground transition hover:border-primary/50 hover:text-primary"
            >
              <Plus className="h-4 w-4" /> Add step
            </button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Fires while the console is open. Offline firing needs the server scheduler (added with integrations).
          </p>
        </div>

        <div className="flex justify-end gap-2 border-t border-border px-5 py-3">
          <button onClick={onClose} className="rounded-lg border border-border px-3 py-1.5 text-sm font-medium transition hover:bg-muted">Cancel</button>
          <button onClick={() => { save(draft); onClose(); }} className="rounded-lg bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground transition hover:opacity-90">Save</button>
        </div>
      </div>
    </div>
  );
}
